import type { CSSProperties } from "react";
import { useLocation } from "react-router";
import { isWideBandArt, publicBandArtFor, publicBandSrc, type PublicBandArt } from "@/lib/public-page-band";
import { isLongBandTitle, PageBand, PageBandImg, type PageBandCrumb } from "./PageBand";

/**
 * 公開の頁の頭の帯。ヘッダーの真下に横幅いっぱいで置き、絵の上 (左) にパンくずと題 (h1) を重ねる。/luagate の帯と同じ部品と CSS (PageBand)。
 * 絵は住所の区画から lib/public-page-band.ts が決める (art で名前を渡せば上書き)。パンくずの頭は「ホーム」で、crumbs はホームと今の頁のあいだの段だけ。
 * 頁の側の h1 と目に見えるパンくずは消す (この帯が h1 を持つ)。公開の頁は暗い版を持たないので、明るい版の .lg で包む
 */
export function PublicPageBand({
  title,
  crumbs = [],
  art,
  column,
  homeLabel = "ホーム",
  crumbsLabel,
}: {
  title: string;
  crumbs?: readonly PageBandCrumb[];
  art?: PublicBandArt;
  /**
   * 下の本文の列 (最大の幅と、768px 以上での左右の余白、px)。渡すと、広い画面で題とパンくずの左の端を本文の左の端にそろえる。
   * 例 PageContainer の 6xl は { max: 1152, pad: 32 }
   */
  column?: { max: number; pad: number };
  /** パンくずの頭の文言。英語のページなど言語を変えたい呼び出し元だけ渡す (既定は日本語のまま) */
  homeLabel?: string;
  /** パンくずの nav の読み上げの名前。英語のページなど言語を変えたい呼び出し元だけ渡す */
  crumbsLabel?: string;
}) {
  const { pathname } = useLocation();
  const name = art ?? publicBandArtFor(pathname);
  const home: PageBandCrumb = { label: homeLabel, to: "/" };
  // --pub-band-sky は、スマホで帯が縦に伸びたときに絵の上を同じ空の色で埋めるため (page-band.css の公開の頁のスマホ)
  const style = {
    "--pub-band-sky": `url("${publicBandSrc(name)}")`,
    ...(column ? { "--pub-band-max": `${column.max}px`, "--pub-band-pad": `${column.pad}px` } : {}),
  } as CSSProperties;
  const classNames = [
    "lg",
    "pub-band",
    column ? "pub-band--column" : null,
    isWideBandArt(name) ? null : "pub-band--fit-right",
    isLongBandTitle(title) ? "pub-band--long-title" : null,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={classNames} data-lg-theme="hp" data-lg-mode="light" style={style}>
      <PageBand name={name} art={<PageBandImg src={publicBandSrc(name)} />} heading={{ title, crumbs, home, crumbsLabel }} />
    </div>
  );
}
